// Frontend plan service — fetches plan/usage from server

import type { PlanTier, PlanInfo, LibraryUsage } from '../types/plan';
import type { DriveAuthSession } from '../types';

const API_BASE = '/api';

const authHeaders = (session: DriveAuthSession) => ({
  Authorization: `Bearer ${session.idToken || session.accessToken}`,
  'x-access-token': session.accessToken,
  'Content-Type': 'application/json',
});

export interface PlanResponse extends PlanInfo {
  usage: LibraryUsage;
}

export const planService = {
  /** Fetch current plan, limits, features, and usage from server */
  async getPlan(session: DriveAuthSession): Promise<PlanResponse> {
    try {
      const res = await fetch(`${API_BASE}/billing/plan`, {
        method: 'GET',
        headers: authHeaders(session),
      });

      if (!res.ok) {
        console.warn('Plan fetch failed, defaulting to free');
        return defaultPlanResponse();
      }

      return await res.json();
    } catch {
      return defaultPlanResponse();
    }
  },

  /** Set plan (dev-only) */
  async setPlan(session: DriveAuthSession, tier: PlanTier): Promise<PlanResponse> {
    const res = await fetch(`${API_BASE}/billing/plan`, {
      method: 'POST',
      headers: authHeaders(session),
      body: JSON.stringify({ tier }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Failed to set plan' }));
      throw new Error(err.error || 'Failed to set plan');
    }

    return await res.json();
  },
};

function defaultPlanResponse(): PlanResponse {
  return {
    tier: 'free',
    limits: {
      driveItemsLimit: 2000,
      localItemsLimit: -1,
      aiSearchTags: false,
      collectionsEnabled: false,
      collectionsLimit: 1,
      premiumFeatures: [],
    },
    features: {
      aiSearch: false,
      collections: false,
      cloudBackup: false,
      smartShuffle: false,
      memoryRecaps: false,
      priorityProcessing: false,
    },
    usage: {
      driveItemsUsed: 0,
      driveItemsLimit: 2000,
      tier: 'free',
    },
  };
}
